import type { AgentKPIs } from '../domain/entities/KPI.js';
import type { Achievement, BonusProjection } from '../domain/entities/Achievement.js';
import type { GamificationRules } from '../domain/entities/GamificationRules.js';
import type { Order } from '../domain/entities/Order.js';
import type { ProductMix } from '../domain/entities/Product.js';
import { pctOfTarget, yoyDelta } from './kpi.js';

/**
 * Extra context the predicates need beyond KPIs.
 * Every field is optional — when missing, the predicate simply degrades
 * (the badge stays locked with 0% progress) instead of crashing.
 */
export interface GamificationContext {
  reactivatedReds?: number;
  productMix?: ProductMix;
  /** Orders within the current month, used to derive ticket medio and
   * dormant-client comebacks. */
  ordersThisMonth?: Order[];
  /** Country-level average ticket for the agent's country, fed in by the
   * use case from peer data (or mock until that arrives). */
  countryAverageTicketEUR?: number;
  /** Margin percentage for the period (0..1). Source-provided when available. */
  marginPct?: number;
  /** How many clients reactivated this month that had been dormant for
   * `comebackDormantMonths` months or more. */
  longDormantComebacks?: number;
}

/**
 * Pure predicates over KPIs + context. Same predicate works no matter who
 * provides the underlying data. Future rules go here as new pure functions.
 */
export function evaluateAchievements(
  kpis: AgentKPIs,
  rules: GamificationRules,
  ctx: GamificationContext = {},
): Achievement[] {
  const revenuePct = pctOfTarget(kpis.revenue);
  const clientsYoY = yoyDelta(kpis.clientCount);
  const reactivated = ctx.reactivatedReds ?? 0;
  const categories = ctx.productMix?.byCategory.length ?? 0;
  const lines = ctx.productMix?.byLine.length ?? 0;
  const avgTicket =
    ctx.ordersThisMonth && ctx.ordersThisMonth.length > 0
      ? ctx.ordersThisMonth.reduce((s, o) => s + o.totalEUR, 0) / ctx.ordersThisMonth.length
      : 0;
  const ticketOverAvg =
    ctx.countryAverageTicketEUR && ctx.countryAverageTicketEUR > 0
      ? avgTicket / ctx.countryAverageTicketEUR - 1
      : 0;

  return rules.badges.map((b): Achievement => {
    switch (b.id) {
      case 'super_vendedor':
        return badge(b, revenuePct >= 1.1, revenuePct / 1.1);
      case 'cazador_clientes':
        return badge(b, clientsYoY >= 0.15, clientsYoY / 0.15);
      case 'ruta_perfecta': {
        const threshold = rules.reactivationThresholdForRutaPerfecta;
        return badge(b, reactivated >= threshold, reactivated / threshold);
      }
      case 'multi_categoria': {
        const goal = rules.minCategoriesForMultiCategoria;
        return badge(b, categories >= goal, categories / goal);
      }
      case 'cross_line': {
        const goal = rules.minLinesForCrossLine;
        return badge(b, lines >= goal, lines / goal);
      }
      case 'ticket_alto': {
        const goal = rules.ticketAltoOverAveragePct;
        return badge(b, ticketOverAvg >= goal, goal > 0 ? ticketOverAvg / goal : 0);
      }
      case 'sello_premium': {
        const goal = rules.selloPremiumMarginPct;
        const margin = ctx.marginPct ?? 0;
        return badge(b, margin >= goal, goal > 0 ? margin / goal : 0);
      }
      case 'comeback_cliente': {
        const comebacks = ctx.longDormantComebacks ?? 0;
        return badge(b, comebacks >= 1, Math.min(1, comebacks));
      }
    }
  });
}

function badge(rule: { id: Achievement['id']; label: string; description: string }, unlocked: boolean, progress: number): Achievement {
  return { id: rule.id, label: rule.label, description: rule.description, unlocked, progress: clamp01(progress) };
}

/**
 * Bonus projection.
 *
 * Models the LOSAN agent compensation as a commission on the season's
 * projected revenue. The rate starts at `baseCommissionPct` (typically 10%)
 * and ramps up linearly to `maxCommissionPct` (typically 12%) when the agent
 * reaches `tieredAtPctOfTarget` of their objective (typically 110%).
 *
 * Pace projection: the season is treated as proportional to time elapsed since
 * its start, which we approximate as the current month within a 6-month
 * campaign. This is a simplification — when LOSAN provides explicit campaign
 * start/end dates per season this should switch to a real elapsed-fraction
 * computation. The simplification keeps the bonus stable for now.
 *
 * Every intermediate number flows into BonusProjection so the UI can render
 * the formula transparently for the agent (and finance).
 */
export function projectBonus(
  kpis: AgentKPIs,
  rules: GamificationRules,
  _now: Date = new Date(),
): BonusProjection {
  const current = Math.max(0, kpis.revenue.current);
  const target = Math.max(0, kpis.revenue.target);

  const pctOfTargetVal = target > 0 ? current / target : 0;

  // Pace: project current pace to the full period. We use `pctOfPeriodElapsed`
  // = 0.5 by default (mid-period); when current already exceeds target we
  // freeze pace at current (no further extrapolation needed).
  const elapsed = 0.5; // 50% of period — replace with real elapsed fraction
  const projectedRevenue =
    current > target ? current : elapsed > 0 ? current / elapsed : current;
  const paceMultiplier = target > 0 ? projectedRevenue / target : 0;

  // Commission ramp: linear between (1.0, base) and (tieredAt, max).
  const { baseCommissionPct, maxCommissionPct, tieredAtPctOfTarget } = rules.bonus;
  let appliedPct: number;
  if (paceMultiplier <= 1) {
    appliedPct = baseCommissionPct;
  } else if (paceMultiplier >= tieredAtPctOfTarget) {
    appliedPct = maxCommissionPct;
  } else {
    const span = tieredAtPctOfTarget - 1;
    const frac = span > 0 ? (paceMultiplier - 1) / span : 0;
    appliedPct = baseCommissionPct + (maxCommissionPct - baseCommissionPct) * frac;
  }

  const projectedBonus = Math.max(rules.bonus.minBonusEUR, projectedRevenue * appliedPct);

  return {
    currentRevenueEUR: Math.round(current),
    targetRevenueEUR: Math.round(target),
    pctOfTarget: round4(pctOfTargetVal),
    projectedRevenueEUR: Math.round(projectedRevenue),
    paceMultiplier: round4(paceMultiplier),
    appliedCommissionPct: round4(appliedPct),
    projectedBonusEUR: Math.round(projectedBonus),
  };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  return n > 1 ? 1 : n;
}
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}
